import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AddParticipantForm, {
  ReconfiguredDepartmentEntity,
} from './AddParticipantForm/AddParticipantForm';
import { ISingleFlag } from '../../constans/flags';
import { transformFormErrors } from '../../helpers/helpers';
import Modal from '../../HOC/Modal/Modal';
import { useFulfillDepartments } from '../../hooks/useFulfillDepartments';
import { useParticipantsTeam } from '../../hooks/useParticipantsTeam';
import { IParticipantsItem } from '../../services/surveys';
import Spinner from '../Spinner/Spinner';

export interface IPostParticipantBodyPreTransform {
  name: string;
  emailAddress: string;
  phoneNo: string;
  preferredLanguage: ISingleFlag;
  team?: string;
  leader?: string;
  role?: string;
  department?: ReconfiguredDepartmentEntity;
}

interface IAddParticipantModal {
  active: boolean;
  refreshList: () => void;
  closeModal: () => void;
  baseLang: string;
  editData?: IParticipantsItem;
  surveyType: string;
}

const AddParticipantModal: FC<IAddParticipantModal> = ({
  active,
  refreshList,
  closeModal,
  editData,
  baseLang,
  surveyType,
}) => {
  const [apiError, setApiError] = useState<{ [key: string]: string }>({});
  const { t } = useTranslation();
  const params = useParams();
  const surveyId = params.surveyId || '1';
  const { submitParticipant, patchParticipantMember } = useParticipantsTeam();
  const { isLoading, departments, loadMore, hasMore, isLoadingMore } = useFulfillDepartments({ active });

  const clickAddModalButton = () => {
    const submitModalForm = document.getElementById('AddParticipantModalCallToAction');
    submitModalForm?.click();
  };

  const resetAndCloseModal = () => {
    setApiError({});
    closeModal();
  };

  const submitForm = async (data: IPostParticipantBodyPreTransform) => {
    try {
      const preferredLanguage = data?.preferredLanguage?.shortName || 'en';
      const department = data?.department?.id;

      const finalData = { ...data, ...{ preferredLanguage }, ...{ department } };

      if (editData) {
        const participantId = editData?.id || '1';
        await patchParticipantMember(surveyId, finalData, participantId);
        refreshList();
        return resetAndCloseModal();
      }
      await submitParticipant(surveyId, finalData);
      refreshList();
      resetAndCloseModal();
    } catch (e) {
      const {
        data: { details },
      } = e;
      const errorData = transformFormErrors(details);
      setApiError(errorData);
    }
  };

  return (
    <Modal
      active={active}
      callToAction={clickAddModalButton}
      closeModal={resetAndCloseModal}
      buttonNames={{
        action: editData
          ? t('viewSurvey.participants.form.save')
          : t('viewSurvey.participants.form.add'),
        cancel: t('viewSurvey.participants.form.cancel'),
      }}
      top={'calc((100vh - 650px) / 2)'}
      title={
        editData
          ? t('viewSurvey.participants.form.editParticipant')
          : t('viewSurvey.participants.form.title')
      }
    >
      {isLoading ? (
        <Spinner size="large" />
      ) : (
        <AddParticipantForm
          baseLang={baseLang}
          submit={submitForm}
          apiError={apiError}
          editData={editData}
          surveyType={surveyType}
          departments={departments}
          loadMoreDepartments={hasMore && !isLoadingMore ? loadMore : undefined}
        />
      )}
    </Modal>
  );
};

export default AddParticipantModal;
